import {PaymentArrangementSetup} from "@indebted/api/paymentArrangement/setup"
import {Box, Slider, Typography} from "@mui/material"
import {styled} from "@mui/material/styles"
import {Trans, useTranslation} from "react-i18next"
import {ChevronBackwardIcon, ChevronForwardIcon} from "@indebted/assets"

import {formatCurrency} from "../../../src/currencyFormatter"
import {ValidLocale} from "../../../src/Locales"

export function NumberOfPaymentSlider({
	setupPage,
	maxDuration,
	sliderValue,
	handleSliderChange,
	handleSliderChangeCommitted,
}: {
	setupPage: PaymentArrangementSetup
	maxDuration: number
	sliderValue: number
	handleSliderChange: (value: number) => void
	handleSliderChangeCommitted: (value: number) => void
}) {
	const {i18n} = useTranslation()
	const _handleSliderChange = (value: number | number[]) => {
		const newValue = typeof value === "number" ? value : value[0]
		if (newValue < 1 || newValue > maxDuration) {
			return
		}
		handleSliderChange(newValue)
	}

	const _handleSliderChangeCommitted = (value: number | number[]) => {
		const newValue = typeof value === "number" ? value : value[0]
		if (newValue < 1 || newValue > maxDuration) {
			return
		}
		handleSliderChangeCommitted(newValue)
	}

	return (
		<PaymentBox>
			<Box sx={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between"}}>
				<SliderButton onClick={() => _handleSliderChangeCommitted(sliderValue - 1)}>
					<ChevronBackwardIcon />
				</SliderButton>

				<Box sx={{paddingBottom: "1rem"}}>
					<Trans
						i18nKey={`payment_arrangement_setup_page.plan_slider`}
						values={{
							PaymentAmount: formatCurrency(
								setupPage.PaymentAmount / 100,
								setupPage.Currency,
								i18n.language as ValidLocale,
							),
							Payments: sliderValue,
							count: sliderValue,
						}}
						components={{
							FirstLine: <PaymentAmountText />,
							SecondLine: <PaymentAmount />,
							ThirdLine: <PaymentText />,
							bold: <strong />,
						}}
					/>
				</Box>
				<SliderButton onClick={() => _handleSliderChangeCommitted(sliderValue + 1)}>
					<ChevronForwardIcon />
				</SliderButton>
			</Box>
			{maxDuration > 1 ? (
				<Box>
					<Box
						sx={{
							width: "100%",
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
							justifyContent: "space-between",
						}}
					>
						<Mark>{1}</Mark>
						<Mark>{maxDuration}</Mark>
					</Box>
					<PaymentSlider
						value={sliderValue}
						min={1}
						max={maxDuration}
						step={1}
						onChange={(_, value) => _handleSliderChange(value)}
						onChangeCommitted={(_, value) => _handleSliderChangeCommitted(value)}
						valueLabelDisplay="auto"
						sx={(theme) => ({
							mt: 2,
							mb: 2,
							height: "0.5rem",
							color: "#474CDE",
							"& .MuiSlider-thumb": {
								transform:
									theme.direction === "rtl"
										? "translate(50%, -50%) !important"
										: "translate(-50%, -50%) !important",
								borderRadius: "624.9375rem",
								background: "#000244",
								boxShadow: "0px 8px 24px 0px rgba(0, 0, 0, 0.05)",
							},
							"& .MuiSlider-markLabel": {
								top: "-1rem",
								transform:
									theme.direction === "rtl"
										? "translateX(100%) !important"
										: "translateX(-100%) !important",
							},
							"& .MuiSlider-mark": {
								width: "0",
								height: "0",
							},
							"& .MuiSlider-rail": {
								borderRadius: "0.5rem",
								background: "#f6f6f6",
								opacity: 1,
							},
						})}
						aria-labelledby="repayment-slider"
					/>
				</Box>
			) : null}
		</PaymentBox>
	)
}

const PaymentBox = styled("div")`
	background: #fff;
	text-align: center;
`

const SliderButton = styled("button")`
	display: flex;
	width: 2rem;
	height: 2rem;
	padding: 0.8125rem;
	justify-content: center;
	align-items: center;
	aspect-ratio: 1/1;
	background: #f6f6f6;
	border-radius: 0.5rem;
	border: none;
	cursor: pointer;

	@media (hover: hover) {
		&:hover {
			background: #e0e0e0;
		}
	}
`

const PaymentAmountText = styled(Typography)`
	color: #000244;
	text-align: center;
	font-size: 0.8125rem;
	font-weight: 400;
	line-height: 1rem;
`

const PaymentAmount = styled(Typography)`
	color: #000244;
	text-align: center;
	font-size: 1.875rem;
	font-weight: 700;
	line-height: 2.25rem;
`

const PaymentText = styled("span")`
	color: #000244;
	text-align: center;
	font-size: 0.8125rem;
	font-weight: 400;
	line-height: 1rem;
	& strong {
		color: #474cde;
		font-weight: 700;
	}
`
const Mark = styled("div")`
	color: #6b7280;
	font-size: 0.75rem;
	font-weight: 400;
	line-height: 1rem;
`
const PaymentSlider = styled(Slider)`
	padding: 0;
`
